import { Check } from "lucide-react";
import { listingCategory } from "@/data/taxonomy";
import { PIN_GLYPH, PIN_LIFT, pinDisc } from "@/lib/mapPins";

const PIN_RULES = [
  [/airport|aeropuerto|sju/i, "✈️", "Airport"],
  [/barbershop|barber/i, "💈", "Barbershop"],
  [/tattoo|piercing/i, "🖋️", "Tattoo"],
  [/hair salon|salon|hair stylist|stylist|beauty/i, "✂️", "Salon"],
  [/nail salon|nails?/i, "💅", "Nails"],
  [/spa|massage|wellness/i, "🧖🏽‍♀️", "Wellness"],
  // A kitchen wins over a bar stool, so "Bar & Grill" places keep the plate.
  [/restaurant|grill|kitchen|dining|cuisine|pizza|taco|criollo|food/i, "🍽️", "Restaurant"],
  [/drink|bar|cocktail|lounge|rooftop|nightlife|club/i, "🍸", "Bar"],
  [/beach|coast|water|surf|snorkel|dive|kayak|park/i, "🏖️", "Beach"],
  [/shop|store|boutique|market|retail|maker|craft|artisan/i, "🛍️", "Shop"],
  [/experience|tour|adventure|activity|excursion/i, "🧭", "Experience"],
  [/culture|museum|art|gallery|history|heritage|landmark|mural/i, "🏛️", "Culture"],
  [/music|dance|theater|theatre|concert/i, "🎵", "Music"],
  [/coffee|café|cafe/i, "☕", "Café"],
  [/bakery|pastry/i, "🥐", "Bakery"],
  [/fitness|gym/i, "🏋️", "Fitness"],
  [/yoga|pilates/i, "🧘🏽‍♀️", "Wellness"],
  [/transport|car rental|moped/i, "🚗", "Transport"],
];

const categoryFallbacks = {
  "Services": ["🛠️", "Local service"],
  "Drink": ["🍸", "Bar"],
  "Gastronomy": ["🍽️", "Food"],
  "Nightlife": ["🎵", "Nightlife"],
  "LGBTQ+": ["🏳️‍🌈", "LGBTQ+"],
  "Eat & Drink": ["🍽️", "Food"],
  "Experiences": ["🧭", "Experience"],
  "Shop": ["🛍️", "Shop"],
  "Culture": ["🏛️", "Culture"],
};

function resolvePin(listing) {
  const sub = String(listing?.subcategory || "").trim();
  const rawCategory = String(listing?.category || listing?.type || "").trim();
  const category = listingCategory(listing) || rawCategory;
  const haystack = `${sub} ${rawCategory} ${listing?.type || ""}`.toLowerCase();

  // Airport always wins so SJU is represented by an airplane, never transport/car.
  const isAirport = /airport|aeropuerto|sju/i.test(`${listing?.name || ""} ${sub} ${rawCategory} ${listing?.type || ""}`);
  if (isAirport) return { emoji: "✈️", label: "Airport" };

  // Granular subcategory matching comes first, so a barbershop or tattoo
  // listing never falls through to the generic Services icon.
  for (const [pattern, emoji, label] of PIN_RULES) {
    if (pattern.test(sub) || pattern.test(haystack)) {
      return { emoji, label };
    }
  }

  return {
    emoji: categoryFallbacks[category]?.[0] || "📍",
    label: categoryFallbacks[category]?.[1] || "Place",
  };
}

export default function PlacePin({ listing, active = false, onClick = undefined, size = 30, density = "detail" }) {
  const { emoji, label } = resolvePin(listing);
  const overview = density === "overview";
  const compact = density === "compact";
  const isAirport = /airport|aeropuerto|sju/i.test(`${listing?.name || ""} ${listing?.subcategory || ""}`);
  // Large enough that the emoji inside is readable at every zoom, small enough
  // that a dense block still shows the places between the pins.
  const pinSize = isAirport ? (overview ? 30 : compact ? 34 : 39) : overview ? 23 : compact ? 28 : 34;
  const emojiSize = isAirport ? (overview ? 18 : compact ? 21 : 24) : overview ? 14 : compact ? 17 : 21;
  const highlighted = Boolean(active || listing?.editor_pick || listing?.featured);
  const disc = pinDisc({ label, active, highlighted });

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${listing?.name || "Place"} — ${label}`}
      title={listing?.name || label}
      className="group relative grid place-items-center transition-transform duration-200 ease-out"
      style={{
        cursor: "pointer",
        background: "transparent",
        border: "none",
        padding: 0,
        width: Math.max(pinSize + 8, 42),
        height: Math.max(pinSize + 8, 42),
        filter: PIN_LIFT,
        transform: active ? "scale(1.15)" : undefined,
        zIndex: active ? 20 : highlighted ? 5 : 1,
      }}
    >
      <span
        className="grid place-items-center rounded-full transition-transform duration-200 ease-out group-hover:scale-[1.15]"
        style={{
          width: pinSize,
          height: pinSize,
          ...disc,
        }}
      >
        <span aria-hidden="true" style={{ ...PIN_GLYPH, fontSize: emojiSize }}>
          {emoji}
        </span>
      </span>

      {listing?.verified && (
        <span
          className="absolute grid place-items-center rounded-full bg-[#ED4956] text-white"
          style={{
            top: 0,
            right: 0,
            width: Math.max(8, Math.round(pinSize * 0.42)),
            height: Math.max(8, Math.round(pinSize * 0.42)),
            border: "1.5px solid #ffffff",
          }}
        >
          <Check style={{ width: "62%", height: "62%" }} strokeWidth={3.5} />
        </span>
      )}
    </button>
  );
}